import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast, ToastOptions } from 'react-toastify';
import { noAutoClose } from 'assets/data/config';
import parseErrorMessage from 'utils/parseErrorMessage';

// Query keys
export const keys = {
  incidents: 'incidents',
  base: 'incident',
} as const;

// Types
interface UpdateOperationalPeriodParams {
  incidentId: string;
  newEndTime?: string;
  operationalPeriod: number;
  phaseNumberCompleted?: number;
  showToast?: boolean;
}

// Helper function for error handling
const handleError = (error: any, errorMessage: string): void => {
  const fullErrorMessage = `${errorMessage}\n\n${parseErrorMessage(error)}\n\nIf you continue to experience this error, contact support@disastertech.com`;
  console.error(fullErrorMessage);
  toast.error(fullErrorMessage, noAutoClose as ToastOptions);
};

export const useUpdateOperationalPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      incidentId,
      newEndTime,
      operationalPeriod,
      phaseNumberCompleted,
    }: UpdateOperationalPeriodParams) => {
      const response = await axios(config.apiGateway.upsertOperationalPeriod, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          new_end_time: newEndTime,
          operational_period: operationalPeriod,
          phase_number_completed: phaseNumberCompleted,
        },
      });
      return response.data;
    },
    onSuccess: (_, { incidentId, showToast = true }) => {
      // Invalidate both the incident data and periods data querie
      queryClient.invalidateQueries({ queryKey: [keys.incidents] });
      queryClient.invalidateQueries({ queryKey: [keys.base, incidentId] });
      queryClient.invalidateQueries({
        queryKey: [keys.base, 'periods', incidentId],
      });
      if (showToast) {
        toast.success('Operational period updated successfully');
      }
    },
    onError: (error: any) => {
      handleError(error, 'Error updating operational period');
    },
  });
};

interface UpdateCurrentOperationalPeriodParams {
  incidentId: string;
  startDateTime: string;
  anticipatedEndDateTime: string | null;
  showToast?: boolean;
}

export const useUpdateCurrentOperationalPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      incidentId,
      startDateTime,
      anticipatedEndDateTime,
    }: UpdateCurrentOperationalPeriodParams) => {
      const response = await axios(
        config.apiGateway.updateCurrentOperationalPeriod,
        {
          method: 'POST',
          data: {
            incident_id: incidentId,
            start_date_time: startDateTime,
            anticipated_end_date_time: anticipatedEndDateTime,
          },
        }
      );
      return response.data;
    },
    onSuccess: (_, { incidentId, showToast = true }) => {
      queryClient.invalidateQueries({ queryKey: [keys.incidents] });
      queryClient.invalidateQueries({ queryKey: [keys.base, incidentId] });
      queryClient.invalidateQueries({
        queryKey: [keys.base, 'periods', incidentId],
      });
      if (showToast) {
        toast.success('Operational period times saved');
      }
    },
    onError: (error: any) => {
      handleError(error, 'Error saving operational period times');
    },
  });
};

interface EscalateIncidentParams {
  incidentId: string;
}

/**
 * Escalates an incident into Planning P without advancing the operational
 * period. The incident stays on period 0 so the first planning cycle prepares
 * operational period 1.
 */
export const useEscalateIncident = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ incidentId }: EscalateIncidentParams) => {
      const response = await axios(config.apiGateway.escalateIncident, {
        method: 'POST',
        data: { incident_id: incidentId },
      });
      return response.data;
    },
    onSuccess: (_, { incidentId }) => {
      queryClient.invalidateQueries({ queryKey: [keys.incidents] });
      queryClient.invalidateQueries({ queryKey: [keys.base, incidentId] });
      queryClient.invalidateQueries({
        queryKey: [keys.base, 'periods', incidentId],
      });
      toast.success('Incident escalated successfully');
    },
    onError: (error: any) => {
      handleError(error, 'Error escalating incident');
    },
  });
};

interface AdvanceOperationalPeriodParams {
  incidentId: string;
  anticipatedEndDateTime: string | null;
  groupGuid: string;
}

export const useAdvanceOperationalPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      incidentId,
      anticipatedEndDateTime,
      groupGuid,
    }: AdvanceOperationalPeriodParams) => {
      const response = await axios(config.apiGateway.advanceOperationalPeriod, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          anticipated_end_date_time: anticipatedEndDateTime,
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    onSuccess: (_, { incidentId }) => {
      // Invalidate both the incident data and periods data queries
      queryClient.invalidateQueries({ queryKey: [keys.incidents] });
      queryClient.invalidateQueries({ queryKey: [keys.base, incidentId] });
      queryClient.invalidateQueries({
        queryKey: [keys.base, 'periods', incidentId],
      });
      toast.success('Operational period advanced successfully');
    },
    onError: (error: any) => {
      handleError(error, 'Error advancing operational period');
    },
  });
};
